import { useMemo, useState } from "react";
import { C, S } from "../customers/theme";
import ProductPicker from "../products/ProductPicker";
import { computeTotals, dueDateFrom, PAYMENT_TERMS, PAYMENT_OPTIONS, CURRENCIES, VAT_RATES } from "../lib/invoices";
import { todayISO } from "../lib/dates";

const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;
const TABS = ["Customer", "Items", "Payment Plan", "Your Business", "Bank Details", "Terms"];

let _seq = 0;
function Field({ label, value, onChange, type = "text", options, placeholder, area }) {
  const id = useMemo(() => `inv-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      {options ? (
        <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : area ? (
        <textarea id={id} style={{ ...S.input, height: 60 }} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input id={id} style={S.input} type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

const blankLine = () => ({ product_id: null, sku: "", description: "", quantity: 1, unit_price: 0, discount_percent: 0, vat_rate: 23, drs: false, drs_amount: 0 });

/**
 * Create/Edit Invoice — the six-tab modal. Pure UI: receives an `initial` draft or a
 * loaded invoice (its Your Business / Bank Details / Terms already snapshotted from
 * Business Settings), plus customers + products. Never touches Supabase directly.
 */
export default function InvoiceForm({ initial, customers = [], products = [], onCancel, onSave }) {
  const seed = initial || {};
  const isEdit = !!seed.id;
  const seedLines = seed.invoice_lines || seed.lines || [];
  const issue0 = seed.issue_date || todayISO();

  const [tab, setTab] = useState("Customer");
  const [f, setF] = useState({
    customer_id: seed.customer_id || "",
    customer_name: seed.customer_name || "",
    customer_email: seed.customer_email || "",
    customer_address: seed.customer_address || "",
    customer_vat: seed.customer_vat || "",
    issue_date: issue0,
    due_date: seed.due_date || dueDateFrom(issue0, seed.payment_terms || "net_30"),
    payment_terms: seed.payment_terms || "net_30",
    preferred_currency: seed.preferred_currency || "EUR",
    status: seed.status || "draft",
    payment_option: seed.payment_option || "full",
  });
  const [lines, setLines] = useState(seedLines.length ? seedLines.map((l) => ({ ...l })) : [blankLine()]);
  const [biz, setBiz] = useState(seed.business_details || {});
  const [bank, setBank] = useState(seed.bank_details || {});
  const [terms, setTerms] = useState({ terms_text: seed.terms_text || "", footer_text: seed.footer_text || "", notes: seed.notes || "" });
  const [schedule, setSchedule] = useState(Array.isArray(seed.payment_schedule) ? seed.payment_schedule : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const totals = computeTotals(lines);
  const cur = f.preferred_currency === "GBP" ? "£" : f.preferred_currency === "USD" ? "$" : "€";
  const settingsMissing = !!seed._settingsMissing;

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const setTermField = (k) => (v) => setTerms((p) => ({ ...p, [k]: v }));
  const setBizField = (k) => (v) => setBiz((p) => ({ ...p, [k]: v }));
  const setBankField = (k) => (v) => setBank((p) => ({ ...p, [k]: v }));
  const onTerms = (v) => setF((p) => ({ ...p, payment_terms: v, due_date: dueDateFrom(p.issue_date, v) }));
  const onIssue = (v) => setF((p) => ({ ...p, issue_date: v, due_date: dueDateFrom(v, p.payment_terms) }));

  const onCustomer = (id) => {
    const cu = customers.find((x) => x.id === id);
    setF((p) => ({
      ...p, customer_id: id || "", customer_name: cu?.company_name || p.customer_name,
      customer_vat: cu?.vat_number || p.customer_vat,
      preferred_currency: cu?.preferred_currency || p.preferred_currency,
      payment_terms: cu?.payment_terms || p.payment_terms,
      due_date: dueDateFrom(p.issue_date, cu?.payment_terms || p.payment_terms),
    }));
  };

  const updLine = (i, patch) => setLines((p) => p.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((p) => [...p, blankLine()]);
  const delLine = (i) => setLines((p) => p.filter((_, j) => j !== i));
  const onPickProduct = (i, prod) => updLine(i, {
    product_id: prod.id, sku: prod.sku || "", description: prod.name,
    unit_price: prod.wholesale_price ?? prod.retail_price ?? prod.unit_price ?? 0, vat_rate: prod.vat_rate ?? 23,
  });

  const addSchedule = () => setSchedule((p) => [...p, { label: "", due_date: f.due_date, amount: 0 }]);
  const updSchedule = (i, patch) => setSchedule((p) => p.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const delSchedule = (i) => setSchedule((p) => p.filter((_, j) => j !== i));

  const submit = async () => {
    setError("");
    if (!f.customer_id && !f.customer_name.trim()) { setError("Select or name a customer (Customer tab)."); setTab("Customer"); return; }
    setBusy(true);
    try {
      await onSave({
        ...f,
        customer_id: f.customer_id || null,
        business_details: biz, bank_details: bank,
        terms_text: terms.terms_text, footer_text: terms.footer_text, notes: terms.notes,
        payment_schedule: f.payment_option === "full" ? [] : schedule,
        lines: lines.map(({ id, line_total, position, ...rest }) => rest),
      });
    } catch (e) { setError(e.message || "Could not save invoice."); setBusy(false); }
  };

  const tabBtn = (t) => ({
    padding: "7px 13px", borderRadius: 7, border: `1px solid ${tab === t ? C.primary : C.border}`, cursor: "pointer",
    fontWeight: 600, fontSize: 12.5, background: tab === t ? C.primaryDim : "transparent", color: tab === t ? C.primary : C.textMuted,
  });

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={{ ...S.modalBox, width: 900 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? "Edit Invoice" : "New Invoice"}
            {seed.invoice_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8, fontFamily: "monospace" }}>{seed.invoice_number}</span>}
            {seed.sales_order_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 12, marginLeft: 8 }}>from {seed.sales_order_number}</span>}
          </h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 16 }}>
          {TABS.map((t) => <button key={t} style={tabBtn(t)} onClick={() => setTab(t)} aria-pressed={tab === t}>{t}</button>)}
        </div>

        {/* ── Customer ─────────────────────────────────────────────── */}
        {tab === "Customer" && (
          <div style={S.grid2}>
            <Field label="Customer" value={f.customer_id} onChange={onCustomer}
              options={[{ value: "", label: customers.length ? "Select customer…" : "No customers yet" }, ...customers.map((cu) => ({ value: cu.id, label: `${cu.company_name}${cu.customer_number ? ` (${cu.customer_number})` : ""}` }))]} />
            <Field label="Customer name *" value={f.customer_name} onChange={set("customer_name")} placeholder="Billed-to name" />
            <Field label="Email" value={f.customer_email} onChange={set("customer_email")} placeholder="billing@customer.ie" />
            <Field label="VAT number" value={f.customer_vat} onChange={set("customer_vat")} />
            <div style={{ gridColumn: "1 / span 2" }}><Field label="Billing address" area value={f.customer_address} onChange={set("customer_address")} /></div>
            <Field label="Issue date" type="date" value={f.issue_date} onChange={onIssue} />
            <Field label="Payment terms" value={f.payment_terms} onChange={onTerms} options={PAYMENT_TERMS} />
            <Field label="Due date" type="date" value={f.due_date} onChange={set("due_date")} />
            <Field label="Currency" value={f.preferred_currency} onChange={set("preferred_currency")} options={CURRENCIES} />
          </div>
        )}

        {/* ── Items ────────────────────────────────────────────────── */}
        {tab === "Items" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 60px 84px 50px 52px 44px 74px 78px 26px", gap: 6, marginBottom: 6, fontSize: 11, fontWeight: 700, color: C.textDim }}>
              {["Description", "SKU", "Qty", "Price", "Disc%", "VAT%", "DRS", "DRS €", "Line Total", ""].map((h, i) => <div key={i}>{h}</div>)}
            </div>
            {lines.map((l, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <ProductPicker products={products} style={{ marginBottom: 5, width: "100%" }} onSelect={(p) => onPickProduct(i, p)} />
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 60px 84px 50px 52px 44px 74px 78px 26px", gap: 6, alignItems: "center" }}>
                  <input style={S.input} value={l.description} placeholder="Description" onChange={(e) => updLine(i, { description: e.target.value })} />
                  <input style={S.input} value={l.sku || ""} placeholder="SKU" onChange={(e) => updLine(i, { sku: e.target.value })} />
                  <input style={S.input} type="number" value={l.quantity} onChange={(e) => updLine(i, { quantity: +e.target.value })} />
                  <input style={S.input} type="number" step="0.01" value={l.unit_price} onChange={(e) => updLine(i, { unit_price: +e.target.value })} />
                  <input style={S.input} type="number" value={l.discount_percent} onChange={(e) => updLine(i, { discount_percent: +e.target.value })} />
                  <select style={S.input} value={l.vat_rate} onChange={(e) => updLine(i, { vat_rate: +e.target.value })}>
                    {VAT_RATES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <input type="checkbox" aria-label="DRS applies" checked={!!l.drs} onChange={(e) => updLine(i, { drs: e.target.checked })} style={{ justifySelf: "center" }} />
                  <input style={{ ...S.input, opacity: l.drs ? 1 : 0.4 }} type="number" step="0.01" value={l.drs_amount} disabled={!l.drs} onChange={(e) => updLine(i, { drs_amount: +e.target.value })} />
                  <input style={{ ...S.input, background: C.surface, color: C.textMuted }} value={money(totals.lines[i]?.line_total, cur)} readOnly tabIndex={-1} />
                  <button style={{ ...S.btn("ghost"), padding: "4px 6px" }} aria-label="Remove line" onClick={() => delLine(i)}>✕</button>
                </div>
              </div>
            ))}
            <button style={S.btn("secondary")} onClick={addLine}>+ Add Line</button>

            <div style={{ background: C.surface2, borderRadius: 8, padding: 14, margin: "16px 0", maxWidth: 320, marginLeft: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Subtotal</span><strong>{money(totals.subtotal, cur)}</strong></div>
              {totals.total_discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Discount</span><strong>−{money(totals.total_discount, cur)}</strong></div>}
              {totals.vat_breakdown.map((b) => (
                <div key={b.rate} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12.5 }}><span style={{ color: C.textMuted }}>VAT @ {b.rate}%</span><span>{money(b.vat, cur)}</span></div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Total VAT</span><strong>{money(totals.total_vat, cur)}</strong></div>
              {totals.total_drs > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>DRS deposit</span><strong>{money(totals.total_drs, cur)}</strong></div>}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}><span>Total</span><span>{money(totals.total_amount, cur)}</span></div>
            </div>
          </div>
        )}

        {/* ── Payment Plan ─────────────────────────────────────────── */}
        {tab === "Payment Plan" && (
          <div>
            <div style={{ maxWidth: 320 }}><Field label="Payment option" value={f.payment_option} onChange={set("payment_option")} options={PAYMENT_OPTIONS} /></div>
            {f.payment_option === "full" ? (
              <div style={{ color: C.textMuted, fontSize: 13, marginTop: 12 }}>Full payment due by <strong style={{ color: C.text }}>{f.due_date || "—"}</strong> ({money(totals.total_amount, cur)}).</div>
            ) : (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 140px 120px 26px", gap: 6, marginBottom: 6, fontSize: 11, fontWeight: 700, color: C.textDim }}>
                  {["Description", "Due date", "Amount", ""].map((h, i) => <div key={i}>{h}</div>)}
                </div>
                {schedule.length === 0 && <div style={{ color: C.textDim, fontSize: 12.5, marginBottom: 8 }}>No installments yet — add the deposit and balance below.</div>}
                {schedule.map((r, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 140px 120px 26px", gap: 6, alignItems: "center", marginBottom: 6 }}>
                    <input style={S.input} value={r.label} placeholder="e.g. Deposit" onChange={(e) => updSchedule(i, { label: e.target.value })} />
                    <input style={S.input} type="date" value={r.due_date || ""} onChange={(e) => updSchedule(i, { due_date: e.target.value })} />
                    <input style={S.input} type="number" step="0.01" value={r.amount} onChange={(e) => updSchedule(i, { amount: +e.target.value })} />
                    <button style={{ ...S.btn("ghost"), padding: "4px 6px" }} aria-label="Remove installment" onClick={() => delSchedule(i)}>✕</button>
                  </div>
                ))}
                <button style={S.btn("secondary")} onClick={addSchedule}>+ Add Installment</button>
              </div>
            )}
          </div>
        )}

        {/* ── Your Business ────────────────────────────────────────── */}
        {tab === "Your Business" && (
          <div>
            {settingsMissing && (
              <div role="alert" style={{ background: C.surface2, border: `1px solid ${C.warning}`, color: C.text, borderRadius: 8, padding: "9px 12px", marginBottom: 12, fontSize: 12.5 }}>
                ⚠ Your business profile isn't set up yet. Fill it here for this invoice, or set it once in <strong>Business Settings</strong> to auto-fill every invoice.
              </div>
            )}
            <div style={S.grid2}>
              <Field label="Legal name" value={biz.legal_name} onChange={setBizField("legal_name")} />
              <Field label="Trading name" value={biz.trading_name} onChange={setBizField("trading_name")} />
              <Field label="VAT number" value={biz.vat_number} onChange={setBizField("vat_number")} />
              <Field label="Registration number" value={biz.registration_number} onChange={setBizField("registration_number")} />
              <Field label="Email" value={biz.email} onChange={setBizField("email")} />
              <Field label="Phone" value={biz.phone} onChange={setBizField("phone")} />
              <Field label="Address line 1" value={biz.address_line1} onChange={setBizField("address_line1")} />
              <Field label="Address line 2" value={biz.address_line2} onChange={setBizField("address_line2")} />
              <Field label="City" value={biz.city} onChange={setBizField("city")} />
              <Field label="County" value={biz.county} onChange={setBizField("county")} />
              <Field label="Postal code" value={biz.postal_code} onChange={setBizField("postal_code")} />
              <Field label="Country" value={biz.country} onChange={setBizField("country")} />
            </div>
          </div>
        )}

        {/* ── Bank Details ─────────────────────────────────────────── */}
        {tab === "Bank Details" && (
          <div style={S.grid2}>
            <Field label="Account name" value={bank.bank_account_name} onChange={setBankField("bank_account_name")} />
            <Field label="Bank name" value={bank.bank_name} onChange={setBankField("bank_name")} />
            <Field label="IBAN" value={bank.iban} onChange={setBankField("iban")} />
            <Field label="BIC" value={bank.bic} onChange={setBankField("bic")} />
            <Field label="Sort code" value={bank.sort_code} onChange={setBankField("sort_code")} />
            <Field label="Account number" value={bank.account_number} onChange={setBankField("account_number")} />
          </div>
        )}

        {/* ── Terms ────────────────────────────────────────────────── */}
        {tab === "Terms" && (
          <div>
            <Field label="Terms & conditions" area value={terms.terms_text} onChange={setTermField("terms_text")} placeholder="Payment terms, ownership, late-payment policy…" />
            <div style={{ marginTop: 10 }}><Field label="Invoice footer" area value={terms.footer_text} onChange={setTermField("footer_text")} placeholder="Thank-you note / registered office line" /></div>
            <div style={{ marginTop: 10 }}><Field label="Notes (internal / on invoice)" area value={terms.notes} onChange={setTermField("notes")} /></div>
          </div>
        )}

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 14 }} role="alert">{error}</div>}

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 20 }}>
          <div style={{ color: C.textMuted, fontSize: 13 }}>Total <strong style={{ color: C.text, fontSize: 15 }}>{money(totals.total_amount, cur)}</strong></div>
          <div style={{ flex: 1 }} />
          <button type="button" style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Create Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}
